import { z } from 'zod';
import { zHypeKind, zId, zJob, zLook, zRoom, zRoomMember, zTimestamp } from './schemas.js';

/**
 * Realtime protocol.
 *
 * Two live surfaces:
 *  - /v1/jobs/:id/stream  — watch a try-on develop, like a polaroid
 *  - /v1/rooms/:code/live — the fitting room: friends reacting in real time
 */

/* ---------------------------- client -> server ---------------------------- */

export const zClientMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ping') }),
  z.object({ type: z.literal('room:hello'), token: z.string() }),
  z.object({ type: z.literal('room:vote'), lookId: zId, kind: zHypeKind, on: z.boolean().default(true) }),
  z.object({ type: z.literal('room:say'), text: z.string().max(240) }),
  z.object({ type: z.literal('room:spotlight'), lookId: zId.nullable() }),
  z.object({ type: z.literal('room:typing'), on: z.boolean() }),
  z.object({ type: z.literal('room:close') }),
]);
export type ClientMessage = z.infer<typeof zClientMessage>;

/* ---------------------------- server -> client ---------------------------- */

export const zRoomChat = z.object({
  id: zId,
  userId: zId,
  username: z.string(),
  text: z.string(),
  createdAt: zTimestamp,
});
export type RoomChat = z.infer<typeof zRoomChat>;

export const zServerMessage = z.discriminatedUnion('type', [
  z.object({ type: z.literal('pong') }),
  z.object({ type: z.literal('error'), code: z.string(), message: z.string() }),

  // job stream
  z.object({ type: z.literal('job:update'), job: zJob }),
  z.object({ type: z.literal('job:done'), job: zJob, look: zLook }),
  z.object({ type: z.literal('job:failed'), job: zJob, message: z.string() }),

  // fitting room
  z.object({ type: z.literal('room:state'), room: zRoom, looks: z.array(zLook), chat: z.array(zRoomChat) }),
  z.object({ type: z.literal('room:joined'), member: zRoomMember }),
  z.object({ type: z.literal('room:left'), userId: zId }),
  z.object({ type: z.literal('room:look'), look: zLook }),
  z.object({
    type: z.literal('room:vote'),
    lookId: zId,
    kind: zHypeKind,
    userId: zId,
    username: z.string(),
    on: z.boolean(),
    tally: z.record(zHypeKind, z.number().int()),
  }),
  z.object({ type: z.literal('room:say'), message: zRoomChat }),
  z.object({ type: z.literal('room:spotlight'), lookId: zId.nullable() }),
  z.object({ type: z.literal('room:typing'), userId: zId, username: z.string(), on: z.boolean() }),
  z.object({ type: z.literal('room:closed') }),
]);
export type ServerMessage = z.infer<typeof zServerMessage>;

export function parseServerMessage(raw: string): ServerMessage | null {
  try {
    const parsed = zServerMessage.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : null;
  } catch {
    return null;
  }
}

export function encodeClientMessage(message: ClientMessage): string {
  return JSON.stringify(message);
}
